import {
  deleteFilterSettingById,
  getFilterSettingListByGame,
} from '@/pages/opinion/services/filter';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bubble,
  Button,
  Modal,
  Select,
  SelectOptionWithGroup,
} from 'tea-component';
import { HistoryType } from '../../types';
import { dealwithHistoryToRender } from '../../utils/historyUtils';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useIntl } from 'umi';
import './index.scss';
import {
  getFilterName,
  saveFilterName,
  setSessionStorage,
} from '../../utils/storage';
import { addHistoryBuriedPoint } from '../../utils/point';

type filterInfoItemType = {
  filter_id: number;
  filter_info: any;
  filter_name: string;
  system: string;
};
/**
 * 使用"**"作为注释的为模块注释
 * 注意点：
 *     删除后只是数据上的删除，并不会去请求过滤器列表，主要兼容本地请求和父级传入这两个情况
 * @param confirm  选中某个过滤器的时候传入的方法
 * @param options  可选参数，如果传入就不再发起请求获取数据，否则就需要发起请求
 * @returns
 */
export default function HistorySelect({
  confirm,
  options,
  isMobile,
  filterName,
  setFilterName,
  gameInfo,
  userInfo,
  setDisabledSaveBtn,
  historyConfig = {},
  finallyDealwithHistoryValue = (value: any) => value,
  historyFilterChange,
}: HistoryType) {
  // TODO: 筛选器保存长度
  const saveFilterLength = 50;
  /** 删除弹窗相关 */
  const [visible, setVisible] = useState(false);
  const toggleModal = () => {
    setVisible(!visible);
  };
  const { formatMessage } = useIntl();
  /** 数据存放与处理 */
  // 删除的过滤器的数据信息，主要用作弹窗展示以及删除请求的id
  const deleteFilterItem = useRef<{ name: string; id: number }>({
    name: '',
    id: -1,
  });
  // select选项数据，不是最终渲染数据
  const [selectOptions, setSelectOptions] = useState<Array<filterInfoItemType>>(
    [],
  );

  useEffect(() => {
    setDisabledSaveBtn(selectOptions.length >= saveFilterLength);
  }, [selectOptions]);

  // 将selectOptions数据转化为select组件使用的格式
  const getOptionsItem = (basic: filterInfoItemType) => {
    const { filter_id, filter_name, filter_info } = basic || {};

    return {
      value: filter_name,
      text: (
        <div className="dbo-select-filter-tab">
          <Bubble dark content={filter_name} closeOnScroll>
            <span className="tab-text">{filter_name}</span>
          </Bubble>

          <Bubble closeOnScroll dark content={formatMessage({ id: 'Delete' })}>
            <i
              className="tab-icon"
              onClick={(e: any) => {
                e?.stopPropagation();
                deleteFilterItem.current = { name: filter_name, id: filter_id };
                setVisible(true);
              }}
            >
              <SvgIcon type="clear-icon" />
            </i>
          </Bubble>
        </div>
      ),
      context: filter_name,
      filter_info: filter_info,
    };
  };
  // select组件最终渲染数据
  const selectOptionsElements = useMemo(() => {
    return selectOptions.map(getOptionsItem);
  }, [selectOptions]);
  // 删除一条记录
  const deleteSelectOptions = (id: number, name: string) => {
    const index = selectOptions.findIndex((item) => item.filter_id === id);
    if (index > -1) {
      selectOptions.splice(index, 1);
      setSelectOptions([...selectOptions]);
    }
    if (filterName === name) {
      setFilterName('');
    }
  };

  // 获取select框选中内容
  const getSelectButton = (val: SelectOptionWithGroup) => {
    return (
      <div className="dbo-select-filter-flex">
        <SvgIcon type="funnel" className="dbo-select-filter-icon" />
        {!filterName ? (
          <span className="dbo-select-filter-text">
            {formatMessage({ id: 'My Filter' })}
          </span>
        ) : (
          <span className="action-text">{(val as any)?.context}</span>
        )}
      </div>
    );
  };

  // 删除过滤器的请求方法
  const deleteSaveFilter = async () => {
    const id = deleteFilterItem.current?.id;
    let { edition_unified_id: uid, id_type: uid_type } = gameInfo;
    const paramsData = { uid, uid_type, filter_id: id };
    await deleteFilterSettingById(paramsData);
    // 删除成功，重新请求或者删除那一项都可以
    deleteSelectOptions(id, deleteFilterItem.current?.name);
    toggleModal();
  };
  // 请求数据，获取数据选项
  const fetchData = async () => {
    let { edition_unified_id: uid, id_type: uid_type } = gameInfo;
    // 删除或新增筛选器配置成功之后重新获取配置列表
    const paramsData = { uid, uid_type, top_n: saveFilterLength };
    if (uid && uid_type) {
      try {
        const res = await getFilterSettingListByGame(paramsData);
        setSelectOptions(res || []);
      } catch (error) {
        console.log(error);
      }
    }
  };

  /** 初始化数据 */
  useEffect(() => {
    // 这里去缓存里拿
    if (options) {
      return setSelectOptions(options);
    }
    if (gameInfo?.edition_unified_id) {
      fetchData();
    }
  }, [options, gameInfo]);

  // 初始化，发现缓存有名称，就选中该过滤器
  useEffect(() => {
    const name = getFilterName(gameInfo);
    if (name) {
      isNoRequest.current = true;
      setFilterName(name);
    }
  }, [gameInfo]);

  // TODO 这里可能造成重复请求问题，待修复
  useEffect(() => {
    if (filterName && !isNoRequest.current) {
      fetchData();
    } else {
      isNoRequest.current = false;
    }
  }, [filterName]);

  // 切换过滤器的情况下不需要请求filter接口的标识
  const isNoRequest = useRef(false);
  /** 父组件操作 */
  // 过滤器修改事件
  const filterChange = (value: string, content: any) => {
    isNoRequest.current = true;
    setFilterName(value);
    let filterInfo: any = null;
    try {
      filterInfo = JSON.parse(content?.option?.filter_info);
    } catch (error) {}
    const filter = dealwithHistoryToRender(filterInfo, isMobile);
    const result = finallyDealwithHistoryValue(
      Object.assign(filter, historyConfig?.fixFilter || {}),
    );

    setSessionStorage(result, gameInfo, true);
    saveFilterName(gameInfo, value, 'D');
    confirm?.(result, content?.option?.context);
    historyFilterChange?.(value, result);
  };
  // 记录select挂载位置
  const historySelectRef = useRef(null);
  return (
    <div
      className={`history-select-wrapper ${
        filterName ? 'dbo-diff-filter' : ''
      }`}
      ref={historySelectRef}
    >
      <Select
        className="dbo-select-filter"
        appearance="button"
        boxClassName="dbo-select-filter-box"
        searchable
        listWidth={230}
        listHeight={220}
        options={selectOptionsElements}
        value={filterName}
        onChange={(value: string, content: any) => {
          filterChange(value, content);
          addHistoryBuriedPoint();
        }}
        footer={
          <div className="dbo-searchbox-label">
            {formatMessage({ id: 'Up to 50 settings can be saved' })}
          </div>
        }
        button={getSelectButton}
      />
      <Modal
        visible={visible}
        onClose={() => {
          setVisible(false);
        }}
      >
        <Modal.Body>
          <div className="dbo-history-deleteSetting-title">
            {formatMessage({
              id: 'Delete the filter setting',
            })}
          </div>
          <div className="dbo-history-deleteSetting-desc">
            {formatMessage({
              id: 'Do you want to delete?',
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="primary" onClick={deleteSaveFilter}>
            {formatMessage({
              id: 'Confirm',
            })}
          </Button>
          <Button type="weak" onClick={() => setVisible(false)}>
            {formatMessage({
              id: 'Cancel',
            })}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
